import { shouldSkipObject } from '@/threejs/objectManager';
import * as THREE from 'three';
import { FullScreenQuad, Pass } from 'three/examples/jsm/postprocessing/Pass.js';
import { CopyShader } from 'three/examples/jsm/shaders/CopyShader.js';
import type { Projector } from './projector';
import type { ProjectorFrustumOverlay } from './projector-frustum-overlay';

export class FrustumOverlayPass extends Pass {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.Camera;
  private readonly frustumOverlay: ProjectorFrustumOverlay;
  private readonly copyMaterial: THREE.ShaderMaterial;
  private readonly fsQuad: FullScreenQuad;
  private projectors: Projector[];
  private overlayEnabled = false;

  constructor(
    scene: THREE.Scene,
    camera: THREE.Camera,
    frustumOverlay: ProjectorFrustumOverlay,
    projectors: Projector[]
  ) {
    super();
    this.scene = scene;
    this.camera = camera;
    this.frustumOverlay = frustumOverlay;
    this.projectors = projectors;
    this.clear = false;
    this.needsSwap = false;
    this.copyMaterial = new THREE.ShaderMaterial({
      uniforms: THREE.UniformsUtils.clone(CopyShader.uniforms),
      vertexShader: CopyShader.vertexShader,
      fragmentShader: CopyShader.fragmentShader,
      depthTest: false,
      depthWrite: false,
    });
    this.copyMaterial.blending = THREE.NoBlending;
    this.fsQuad = new FullScreenQuad(this.copyMaterial);
  }

  setProjectors(projectors: Projector[]) {
    this.projectors = projectors;
  }

  setEnabled(enabled: boolean) {
    this.overlayEnabled = enabled;
    this.enabled = enabled;
  }

  getEnabled() {
    return this.enabled;
  }

  render(renderer: THREE.WebGLRenderer, writeBuffer: THREE.WebGLRenderTarget, readBuffer: THREE.WebGLRenderTarget) {
    const previousAutoClear = renderer.autoClear;
    const previousBackground = this.scene.background;
    renderer.autoClear = false; // Set this FIRST before setRenderTarget

    try {
      renderer.setRenderTarget(readBuffer);

      const visibleProjectors = this.projectors.filter((p) => p.visible);
      if (this.overlayEnabled && visibleProjectors.length > 0) {
        const batchSize = this.frustumOverlay.getBatchSize();
        this.frustumOverlay.beginOverlayPass();

        // Avoid background force-clear while layering overlays.
        this.scene.background = null;

        const hiddenObjects: THREE.Object3D[] = [];
        this.scene.traverse((object: THREE.Object3D) => {
          if (object.visible && shouldSkipObject(object)) {
            object.visible = false;
            hiddenObjects.push(object);
          }
        });

        for (let i = 0; i < visibleProjectors.length; i += batchSize) {
          const batch = visibleProjectors.slice(i, i + batchSize);
          this.frustumOverlay.update(renderer, this.scene, batch, {
            overlayOnly: true,
          });
          renderer.render(this.scene, this.camera);
        }

        hiddenObjects.forEach((object) => (object.visible = true));

        this.frustumOverlay.endOverlayPass();
      }
    } finally {
      this.scene.background = previousBackground;
      renderer.autoClear = previousAutoClear;
    }

    if (this.renderToScreen) {
      this.copyMaterial.uniforms['tDiffuse'].value = readBuffer.texture;
      renderer.setRenderTarget(null);
      this.fsQuad.render(renderer);
    }
  }
}
